import PlusCircleOutlined from '@ant-design/icons/PlusCircleOutlined'
import { Button, Tag } from 'antd'
import { CreateTicketsForGroupModal } from 'features/libra/dashboard/CreateTicketsForGroupModal'
import { LibraDashboardConfig } from 'features/libra/dashboard/libraDashboardApi'
import {
  LIBRA_TICKET_GROUP_ENTRY_TYPE,
  LibraTicketGroup,
  useLibraTickets,
} from 'features/libra/ticket/libraTicketsApi'
import { useRolodexEntries } from 'features/rolodex'
import { FC, useMemo, useState } from 'react'
import styled from 'styled-components'
import { RolodexEntryType } from 'types'

import { AssetListView } from './AssetListView'
import { DashboardSummaryCharts } from './DashboardSummaryCharts'
import { ProgressKanban } from './ProgressKanban'
import { TicketDetailView } from './TicketDetailView'

export const LibraDashboardContent: FC<{
  config: LibraDashboardConfig
  groups: ReadonlyArray<LibraTicketGroup>
}> = ({ config, groups }) => {
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [selectedAssetId, setSelectedAssetId] = useState<string | undefined>(
    undefined
  )
  const [selectedSiteId, setSelectedSiteId] = useState<string | undefined>(
    undefined
  )

  // Query tickets by the selected groups
  const {
    tickets,
    isLoading: ticketsLoading,
    forceRefresh,
  } = useLibraTickets(
    useMemo(
      () => ({
        [LIBRA_TICKET_GROUP_ENTRY_TYPE]: groups.map((group) => group.id),
      }),
      [groups]
    )
  )

  const organizationId = config.tags?.organization as string | undefined
  const firstGroup = groups[0]

  // Get unique site IDs
  const siteIds = useMemo(
    () =>
      Array.from(
        new Set(
          tickets
            .map((ticket) => ticket.tags.site)
            .filter((site): site is string => site !== undefined)
        )
      ),
    [tickets]
  )

  // Query sites
  const { entries: siteEntries } = useRolodexEntries(
    useMemo(
      () => ({
        type: RolodexEntryType.SITE,
        ids: siteIds,
      }),
      [siteIds]
    )
  )

  // Filter tickets by selected site
  const filteredTickets = useMemo(() => {
    if (!selectedSiteId) return tickets
    return tickets.filter((ticket) => ticket.tags.site === selectedSiteId)
  }, [selectedSiteId, tickets])

  // Filter tickets for selected asset
  const selectedAssetTickets = useMemo(() => {
    if (!selectedAssetId) return []
    return filteredTickets.filter(
      (ticket) => ticket.tags.unit === selectedAssetId
    )
  }, [selectedAssetId, filteredTickets])

  return (
    <>
      <ContentWrapper>
        <TopRow>
          <SiteFiltersContainer>
            {siteEntries.length > 0 && (
              <>
                <SiteFiltersLabel>Sites:</SiteFiltersLabel>
                <SitePills>
                  <SiteTag
                    $isSelected={!selectedSiteId}
                    onClick={() => setSelectedSiteId(undefined)}>
                    All Sites
                  </SiteTag>
                  {siteEntries.map((site) => (
                    <SiteTag
                      key={site.id}
                      $isSelected={selectedSiteId === site.id}
                      onClick={() =>
                        setSelectedSiteId(
                          selectedSiteId === site.id ? undefined : site.id
                        )
                      }>
                      {site.data.name as string}
                    </SiteTag>
                  ))}
                </SitePills>
              </>
            )}
          </SiteFiltersContainer>
          <ButtonContainer>
            <Button
              type='primary'
              icon={<PlusCircleOutlined />}
              onClick={() => setIsCreateModalOpen(true)}
              disabled={!organizationId || !firstGroup}>
              Create Tickets
            </Button>
          </ButtonContainer>
        </TopRow>
        {organizationId && (
          <ChartsFullWidth>
            <DashboardSummaryCharts
              tickets={tickets}
              organizationId={organizationId}
            />
          </ChartsFullWidth>
        )}
        <ProgressKanban tickets={filteredTickets} />
        <MainContent>
          {organizationId && (
            <AssetListView
              tickets={filteredTickets}
              selectedAssetId={selectedAssetId}
              setSelectedAssetId={setSelectedAssetId}
              isLoading={ticketsLoading}
            />
          )}
          <TicketDetailView tickets={selectedAssetTickets} />
        </MainContent>
      </ContentWrapper>
      {organizationId && firstGroup && (
        <CreateTicketsForGroupModal
          isOpen={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={forceRefresh}
          organizationId={organizationId}
          groupId={firstGroup.id}
          groupTitle={firstGroup.title}
        />
      )}
    </>
  )
}

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  height: 100%;
  min-height: 0;
`

const TopRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
`

const SiteFiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex: 1;
  min-width: 0;
`

const ButtonContainer = styled.div`
  flex-shrink: 0;
`

const ChartsFullWidth = styled.div`
  width: 100%;
  flex-shrink: 0;
`

const SiteFiltersLabel = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.forecast.activeText};
  font-size: 0.875rem;
  white-space: nowrap;
`

const SitePills = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const SiteTag = styled(Tag)<{ $isSelected: boolean }>`
  cursor: pointer;
  margin: 0;
  padding: 4px 12px;
  border-radius: 16px;
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  border-color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.forecast.primary : theme.colors.gray[700]};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? '#fff' : theme.forecast.activeText};

  &:hover {
    background: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    border-color: ${({ theme, $isSelected }) =>
      $isSelected ? theme.forecast.primary : theme.colors.gray[600]};
    color: ${({ theme, $isSelected }) =>
      $isSelected ? '#fff' : theme.forecast.activeText};
  }
`

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 2fr 3fr;
  gap: 1rem;
  flex: 1;
  min-height: 0;
  overflow: hidden;
`
